import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL, CATEGORY_NAME, fmtDuration, fmtBytes } from "@/lib/api";
import {
  ArrowLeft, Trophy, Calendar, Film, Share2, Copy, Check, Play, X,
  MessageSquare, Send, Eye, Heart, Sparkles, TrendingUp, Activity,
  Search, Star, Pencil, Camera, Pin, Wand2, Brain, Trash2, Plus,
} from "lucide-react";
import { ClipCardWithActions } from "@/components/ClipCard";

// Convert any avatar reference into a usable image URL:
// • already-absolute http(s) → return as-is (legacy bare-S3 entries still
//   resolve as long as the bucket allows it; broken ones look broken)
// • /api/avatar/<key> relative → prepend BACKEND_URL
// • bare S3 key (avatars/...) → wrap in our backend redirect endpoint
function resolveAvatar(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) {
    // Old format: parse the key out and route through our backend so private
    // buckets still serve. Recognise `https://<bucket>.s3.<region>.amazonaws.com/<key>`
    const m = u.match(/amazonaws\.com\/(.+)$/);
    if (m && m[1].startsWith("avatars/")) {
      return `${BACKEND_URL}/api/avatar/${encodeURIComponent(m[1])}`;
    }
    return u;
  }
  if (u.startsWith("/api/avatar/")) return `${BACKEND_URL}${u}`;
  if (u.startsWith("avatars/") || u.startsWith("training_frames/")) {
    return `${BACKEND_URL}/api/avatar/${encodeURIComponent(u)}`;
  }
  return u;
}

const REACTIONS = [
  { id: "fire", emoji: "🔥" },
  { id: "clap", emoji: "👏" },
  { id: "heart", emoji: "❤️" },
  { id: "wow", emoji: "🤠" },
  { id: "rope", emoji: "🪢" },
];
const REACTION_EMOJI = Object.fromEntries(REACTIONS.map((r) => [r.id, r.emoji]));

const fmtAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

export default function RiderProfile() {
  return <RiderProfileInner shareMode={false} />;
}

export function RiderProfileInner({ data: initialData = null, shareMode = false }) {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [previewClip, setPreviewClip] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [frameCount, setFrameCount] = useState(0);

  const refreshFrameCount = (riderName) => {
    api.get(`/rider/${encodeURIComponent(riderName)}/training-frames`)
      .then((r) => setFrameCount((r.data.frames || []).length))
      .catch(() => {});
  };

  useEffect(() => {
    const target = name || data?.name;
    if (target) refreshFrameCount(target);
  }, [name, data?.name]);

  const reload = () => {
    if (!name && !data?.name) return;
    const target = name || data?.name;
    api.get(`/rider/${encodeURIComponent(target)}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (initialData) return;
    if (!name) return;
    setLoading(true);
    api.get(`/rider/${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Rider not found"))
      .finally(() => setLoading(false));
  }, [name, initialData]);

  const handleShare = async () => {
    try {
      const res = await api.post("/share", { rider_name: data.name });
      const fullUrl = `${window.location.origin}/share/${res.data.token}`;
      setShareUrl(fullUrl);
      try {
        await navigator.clipboard.writeText(fullUrl);
        setShareCopied(true);
        toast.success("Share link copied to clipboard");
        setTimeout(() => setShareCopied(false), 2500);
      } catch {
        toast.info("Link generated — copy below");
      }
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const [runSearch, setRunSearch] = useState("");
  const filteredEvents = useMemo(() => {
    const events = data?.events || [];
    const q = runSearch.trim().toLowerCase();
    if (!q) return events;
    return events
      .map((ev) => ({
        ...ev,
        clips: ev.clips.filter((c) => {
          const hay = [
            c.rider_horse_name, c.heeler_name, c.horse_name, c.owner_name,
            c.sub_label, c.notes, c.source_video_name,
            ev.event_name, ev.event_date,
            CATEGORY_NAME[c.category] || c.category,
          ].filter(Boolean).join(" ").toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((ev) => ev.clips.length > 0);
  }, [data, runSearch]);

  if (loading) return <div className="p-12 text-center text-stone-500">Loading dashboard…</div>;
  if (!data) {
    return (
      <div className="p-12 text-center text-stone-500">
        Rider not found.
        {!shareMode && <div className="mt-4"><Link to="/riders" className="btn-secondary">← Riders</Link></div>}
      </div>
    );
  }

  const { stats, top_clips: topClips = [], recent_comments: recentComments = [], recent_reactions: recentReactions = [], reels = [], events = [], profile = {}, pinned_clip: pinnedClip = null, performance_trend: trend = [] } = data || {};
  // Merge & sort recent activity (comments + reactions) for the fan wall
  const activity = [
    ...recentComments.map((c) => ({ ...c, kind: "comment" })),
    ...recentReactions.map((r) => ({ ...r, kind: "reaction" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 30);

  const reactionEntries = Object.entries(stats.reactions_breakdown || {}).filter(([, n]) => n > 0);

  const handlePin = async (clipId) => {
    try {
      await api.put(`/rider/${encodeURIComponent(data.name)}/profile`, {
        pinned_clip_id: profile.pinned_clip_id === clipId ? "" : clipId,
      });
      toast.success(profile.pinned_clip_id === clipId ? "Unpinned" : "Pinned as hero clip");
      reload();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const buildBestReel = async () => {
    try {
      const r = await api.post(`/rider/${encodeURIComponent(data.name)}/best-reel`);
      toast.success(`Building reel of ${r.data.clip_count} clips — heading to Reels…`);
      setTimeout(() => navigate("/reels"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to build reel");
    }
  };

  return (
    <div className="px-6 md:px-12 py-8 max-w-[1500px]" data-testid="rider-profile">
      {/* Header */}
      <div className="flex items-start gap-5 mb-6 pb-5 border-b border-stone-800">
        {/* Avatar */}
        <div className="shrink-0 relative group">
          {profile.avatar_url ? (
            <img src={resolveAvatar(profile.avatar_url)} alt={data.name} className="w-24 h-24 sm:w-32 sm:h-32 object-cover border-2 border-orange-600" data-testid="rider-avatar" />
          ) : (
            <div className="w-24 h-24 sm:w-32 sm:h-32 bg-stone-900 border-2 border-stone-800 flex items-center justify-center font-display uppercase text-4xl font-bold text-orange-500" data-testid="rider-avatar-placeholder">
              {(data.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase()}
            </div>
          )}
          {!shareMode && (
            <button
              onClick={() => setShowProfileEdit(true)}
              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs uppercase tracking-widest"
              data-testid="edit-avatar-btn"
            >
              <Camera size={20} />
            </button>
          )}
        </div>
        <div className="flex-1 min-w-0 border-l-4 border-orange-600 pl-5">
          {!shareMode && (
            <button onClick={() => navigate("/riders")} className="btn-secondary inline-flex items-center gap-1.5 mb-3 !text-xs" data-testid="back-to-riders">
              <ArrowLeft size={12} /> All Riders
            </button>
          )}
          <div className="label-tiny mb-2">Rider Dashboard</div>
          <h1 className="font-display uppercase text-4xl sm:text-5xl font-bold leading-[0.95] truncate" data-testid="rider-name">
            {data.name}
          </h1>
          {profile.bio && (
            <p className="text-stone-300 mt-3 max-w-2xl text-sm leading-relaxed">{profile.bio}</p>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {!shareMode && (
            <button onClick={buildBestReel} className="btn-secondary inline-flex items-center gap-1.5 text-xs hover:!border-orange-700 hover:!text-orange-400" data-testid="best-reel-btn" title="Auto-build a reel from this rider's thumbs-up'd clips">
              <Wand2 size={14} /> Best Runs Reel
            </button>
          )}
          {!shareMode && (
            <button onClick={() => setShowTrainModal(true)} className="btn-secondary inline-flex items-center gap-1.5 text-xs hover:!border-emerald-700 hover:!text-emerald-400" data-testid="train-rider-btn" title="Teach the AI to recognize this rider by saving reference frames from their clips">
              <Brain size={14} /> Train AI ({frameCount})
            </button>
          )}
          {!shareMode && (
            <button onClick={() => setShowProfileEdit(true)} className="btn-secondary inline-flex items-center gap-1.5 text-xs" data-testid="edit-profile-btn">
              <Pencil size={12} /> Edit Profile
            </button>
          )}
          {!shareMode && (
            <button onClick={handleShare} className="btn-primary inline-flex items-center gap-1.5 text-xs" data-testid="share-rider-btn">
              <Share2 size={14} /> Share
            </button>
          )}
        </div>
      </div>

      {shareUrl && (
        <div className="bg-stone-900 border border-orange-700 px-4 py-3 mb-6 flex items-center gap-3" data-testid="share-url-box">
          <Share2 size={14} className="text-orange-500 shrink-0" />
          <input value={shareUrl} readOnly className="field-input flex-1 !bg-stone-950" />
          <button
            onClick={() => { navigator.clipboard.writeText(shareUrl); setShareCopied(true); setTimeout(() => setShareCopied(false), 2500); }}
            className="btn-secondary inline-flex items-center gap-1.5"
            data-testid="copy-share-btn"
          >
            {shareCopied ? <Check size={12} /> : <Copy size={12} />}
            {shareCopied ? "Copied" : "Copy"}
          </button>
        </div>
      )}

      {/* Pinned hero clip */}
      {pinnedClip && (
        <section className="mb-8 border border-orange-700/40 bg-stone-900/30 overflow-hidden" data-testid="pinned-hero">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-stone-800 bg-stone-900/50">
            <Pin size={12} className="text-orange-500" />
            <span className="label-tiny">Featured Run</span>
            {!shareMode && (
              <button onClick={() => handlePin(pinnedClip.id)} className="ml-auto text-stone-500 hover:text-rose-400 text-xs" data-testid="unpin-hero-btn">
                Unpin
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            <div className="lg:col-span-2 bg-black">
              <video src={`${BACKEND_URL}/api/clips/${pinnedClip.id}/stream`} className="w-full" controls preload="metadata" playsInline />
            </div>
            <div className="p-5 flex flex-col">
              <span className="tag-cat self-start">{CATEGORY_NAME[pinnedClip.category]}</span>
              <div className="font-display uppercase text-3xl font-bold leading-tight mt-3">{pinnedClip.sub_label || "Run"}</div>
              {pinnedClip.notes && <p className="text-stone-300 text-sm mt-3 italic">{pinnedClip.notes}</p>}
              <div className="mt-auto pt-4 flex items-center gap-3 font-mono-tc text-xs text-stone-500">
                <span><Eye size={11} className="inline mr-1" /> {pinnedClip.views || 0}</span>
                <span><MessageSquare size={11} className="inline mr-1" /> {pinnedClip.comments_count || 0}</span>
                <span className="ml-auto text-orange-400">{fmtDuration(pinnedClip.duration)}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="rider-hero-stats">
        <StatCard label="Views" value={stats.total_views} icon={<Eye size={14} />} />
        <StatCard label="Reactions" value={stats.total_reactions} icon={<Heart size={14} />} accent="rose" />
        <StatCard label="Comments" value={stats.total_comments} icon={<MessageSquare size={14} />} accent="emerald" />
        <StatCard label="Clips" value={stats.total_clips} icon={<Film size={14} />} />
        <StatCard label="Reels" value={stats.reels_count} icon={<Sparkles size={14} />} accent="orange" />
        <StatCard label="Events" value={stats.events_count} icon={<Calendar size={14} />} />
      </div>

      {/* Reactions breakdown bar */}
      {reactionEntries.length > 0 && (
        <div className="border border-stone-800 px-4 py-3 mb-8" data-testid="reactions-breakdown">
          <div className="label-tiny mb-2 flex items-center gap-2">
            <Activity size={12} /> Reaction Breakdown
          </div>
          <div className="flex flex-wrap gap-3">
            {reactionEntries.map(([emo, n]) => (
              <div key={emo} className="inline-flex items-center gap-2 border border-stone-800 bg-stone-900/60 px-3 py-1.5">
                <span className="text-lg">{REACTION_EMOJI[emo] || ""}</span>
                <span className="font-mono-tc text-stone-200">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance trend */}
      {trend.length > 0 && (
        <PerformanceTrend trend={trend} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Top clips by engagement */}
          {topClips.length > 0 && (
            <section data-testid="top-clips-section">
              <h2 className="font-display uppercase text-2xl font-bold mb-3 flex items-center gap-2">
                <TrendingUp size={18} className="text-orange-500" /> Top Clips
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {topClips.slice(0, 4).map((c) => (
                  <RiderClipCard key={c.id} clip={c} onPreview={() => setPreviewClip(c)} onPin={shareMode ? null : () => handlePin(c.id)} isPinned={profile.pinned_clip_id === c.id} />
                ))}
              </div>
            </section>
          )}

          {/* Reels */}
          {reels.length > 0 && (
            <section data-testid="rider-reels-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display uppercase text-2xl font-bold flex items-center gap-2">
                  <Sparkles size={18} className="text-orange-500" /> Reels Featuring This Rider
                </h2>
                {!shareMode && <Link to="/reels" className="text-xs text-stone-400 hover:text-orange-500 uppercase tracking-wider">All reels →</Link>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {reels.slice(0, 4).map((r) => (
                  <div key={r.id} className="border border-stone-800 hover:border-orange-700 transition-colors" data-testid={`rider-reel-${r.id}`}>
                    {r.status === "done" ? (
                      <video src={r.stream_url || `${BACKEND_URL}/api/reels/${r.id}/stream`} className="w-full aspect-video bg-black" controls preload="metadata" />
                    ) : (
                      <div className="w-full aspect-video bg-black flex items-center justify-center text-stone-500 text-xs uppercase tracking-widest">
                        {r.status}
                      </div>
                    )}
                    <div className="p-3">
                      <div className="font-display uppercase font-semibold truncate">{r.title}</div>
                      <div className="font-mono-tc text-xs text-stone-500 mt-1">
                        {r.clip_count} run{r.clip_count !== 1 ? "s" : ""}{r.duration ? ` · ${fmtDuration(r.duration)}` : ""}{r.size_bytes ? ` · ${fmtBytes(r.size_bytes)}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* All clips by event */}
          <section data-testid="all-clips-section">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h2 className="font-display uppercase text-2xl font-bold flex items-center gap-2">
                <Film size={18} className="text-orange-500" /> All Runs
              </h2>
              {!shareMode && (
                <div className="relative flex-1 min-w-[220px] max-w-md ml-auto">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
                  <input
                    type="text"
                    value={runSearch}
                    onChange={(e) => setRunSearch(e.target.value)}
                    placeholder="Search event, horse, notes…"
                    className="field-input w-full !pl-9"
                    data-testid="rider-run-search"
                  />
                </div>
              )}
            </div>
            <div className="space-y-6">
              {filteredEvents.length === 0 && runSearch ? (
                <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500">
                  No runs match "{runSearch}"
                </div>
              ) : filteredEvents.map((ev, idx) => (
                <div key={idx} data-testid={`event-section-${idx}`}>
                  <div className="flex items-baseline gap-3 border-b border-stone-800 pb-2 mb-3">
                    <h3 className="font-display uppercase text-lg font-bold">{ev.event_name}</h3>
                    {ev.event_date && (
                      <span className="font-mono-tc text-orange-400 text-sm">{ev.event_date}</span>
                    )}
                    <span className="font-mono-tc text-stone-500 text-xs ml-auto">{ev.clips.length} run{ev.clips.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {ev.clips.map((c) => (
                      shareMode
                        ? <RiderClipCard key={c.id} clip={c} onPreview={() => setPreviewClip(c)} onPin={null} isPinned={profile.pinned_clip_id === c.id} />
                        : <ClipCardWithActions key={c.id} clip={c} onChanged={reload} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right column: Fan wall / activity feed */}
        <aside className="lg:sticky lg:top-4 lg:self-start space-y-4">
          <section className="border border-stone-800" data-testid="fan-wall">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-800 bg-stone-900/60">
              <Activity size={14} className="text-orange-500" />
              <div className="font-display uppercase font-semibold">Fan Wall</div>
              <span className="font-mono-tc text-stone-500 text-xs ml-auto">{activity.length}</span>
            </div>
            {activity.length === 0 ? (
              <div className="px-4 py-8 text-center text-stone-500 text-sm italic">
                No fan activity yet.<br />Share your dashboard to start collecting reactions and comments.
              </div>
            ) : (
              <div className="max-h-[640px] overflow-y-auto divide-y divide-stone-800/60">
                {activity.map((a, i) => (
                  <div key={i} className="px-4 py-3 hover:bg-stone-900/30 cursor-pointer transition-colors"
                       onClick={() => {
                         const allClips = events.flatMap((e) => e.clips);
                         const clip = allClips.find((c) => c.id === a.clip_id) || topClips.find((c) => c.id === a.clip_id);
                         if (clip) setPreviewClip(clip);
                       }}
                       data-testid={`activity-${i}`}>
                    {a.kind === "comment" ? (
                      <>
                        <div className="text-xs text-stone-500 font-mono-tc mb-1 flex items-center gap-2">
                          <MessageSquare size={10} className="text-emerald-400" />
                          <span className="text-orange-400">{a.by_name}</span>
                          <span className="ml-auto">{fmtAgo(a.created_at)}</span>
                        </div>
                        <div className="text-sm text-stone-200 line-clamp-2">{a.text}</div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">on “{a.clip_label}”</div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-stone-500 font-mono-tc mb-1 flex items-center gap-2">
                          <span className="text-lg leading-none">{REACTION_EMOJI[a.emoji] || ""}</span>
                          <span className="text-orange-400">{a.by_name || "Anonymous"}</span>
                          <span className="ml-auto">{fmtAgo(a.created_at)}</span>
                        </div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-widest">reacted to “{a.clip_label}”</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {stats.clean_runs > 0 && (
            <section className="border border-stone-800 px-4 py-3" data-testid="trophy-card">
              <div className="label-tiny mb-2">Trophy Case</div>
              <div className="flex items-center gap-2">
                <Trophy size={20} className="text-emerald-400" />
                <span className="font-display text-3xl font-bold text-emerald-400">{stats.clean_runs}</span>
                <span className="text-stone-300">clean run{stats.clean_runs !== 1 ? "s" : ""}</span>
              </div>
            </section>
          )}
        </aside>
      </div>

      {previewClip && (
        <ClipFeedbackModal
          clip={previewClip}
          onClose={() => setPreviewClip(null)}
          onChanged={reload}
        />
      )}

      {showProfileEdit && (
        <EditRiderProfileModal
          name={data.name}
          profile={profile}
          onClose={() => setShowProfileEdit(false)}
          onSaved={() => { setShowProfileEdit(false); reload(); }}
        />
      )}

      {showTrainModal && (
        <TrainRiderModal
          name={data.name}
          clips={(data.events || []).flatMap((ev) => ev.clips || []).concat(data.top_clips || [])}
          onClose={() => { setShowTrainModal(false); refreshFrameCount(data.name); }}
        />
      )}
    </div>
  );
}

function TrainRiderModal({ name, clips, onClose }) {
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // clip_id being added

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/rider/${encodeURIComponent(name)}/training-frames`);
      setFrames(r.data.frames || []);
    } catch {
      // empty fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  const addFromClip = async (clipId, label) => {
    if (frames.length >= 12) {
      toast.error("Library full — remove a frame first (max 12)");
      return;
    }
    setAdding(clipId);
    try {
      const r = await api.post(`/rider/${encodeURIComponent(name)}/training-frames`, {
        clip_id: clipId,
        label,
      });
      setFrames((prev) => [...prev, r.data.frame]);
      toast.success("Reference frame added");
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setAdding(null);
    }
  };

  const removeFrame = async (frameId) => {
    if (!window.confirm("Remove this reference frame?")) return;
    try {
      await api.delete(`/rider/${encodeURIComponent(name)}/training-frames/${frameId}`);
      setFrames((prev) => prev.filter((f) => f.id !== frameId));
    } catch (err) {
      toast.error("Failed to remove: " + (err?.response?.data?.detail || err.message));
    }
  };

  // Deduplicate clips by id (top_clips + events can overlap)
  const uniqueClips = Array.from(new Map((clips || []).map((c) => [c.id, c])).values()).slice(0, 24);
  const usedClipIds = new Set(frames.map((f) => f.source_clip_id));

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="train-rider-modal">
      <div className="bg-stone-950 border border-stone-700 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div className="font-display uppercase text-xl flex items-center gap-2">
            <Brain size={16} className="text-emerald-400" /> Train AI on {name}
            <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-700/40 px-1.5 py-0.5">v2 · Identity Library</span>
          </div>
          <button onClick={onClose} className="btn-secondary" data-testid="train-close"><X size={14} /></button>
        </div>

        <div className="px-4 py-3 text-xs text-stone-400 border-b border-stone-800">
          Pick 3–5 favorite clips of <span className="text-stone-200">{name}</span> showing them from different angles (front, side, exiting). The AI saves a reference frame from each and uses them to auto-recognize this rider in future videos.
        </div>

        {/* Existing frames */}
        <div className="px-4 pt-4">
          <div className="label-tiny mb-2">Saved Reference Library · {frames.length} / 12</div>
          {loading ? (
            <div className="text-xs text-stone-500 italic">Loading…</div>
          ) : frames.length === 0 ? (
            <div className="text-xs text-stone-500 italic mb-2">No reference frames yet. Add some below.</div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3" data-testid="train-frame-gallery">
              {frames.map((f) => (
                <div key={f.id} className="relative border border-emerald-700/40 bg-stone-900 aspect-video group">
                  {f.preview_url ? (
                    <img src={f.preview_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-stone-800" />
                  )}
                  <button
                    onClick={() => removeFrame(f.id)}
                    className="absolute top-1 right-1 p-1 bg-stone-900/85 hover:bg-rose-900 text-stone-300 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove this reference"
                    data-testid={`train-frame-remove-${f.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                  {f.label && (
                    <div className="absolute bottom-0 left-0 right-0 bg-emerald-900/85 text-[9px] uppercase tracking-widest text-emerald-100 px-1 py-0.5 truncate">{f.label}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Source clips to add from */}
        <div className="px-4 pt-2 pb-4">
          <div className="label-tiny mb-2">Add from this rider's clips</div>
          {uniqueClips.length === 0 ? (
            <div className="text-xs text-stone-500 italic">This rider has no clips yet — once you generate some, you'll be able to train the AI on them.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="train-source-grid">
              {uniqueClips.map((c) => {
                const used = usedClipIds.has(c.id);
                const isAdding = adding === c.id;
                return (
                  <div key={c.id} className={`border ${used ? "border-emerald-700/40 bg-emerald-900/10" : "border-stone-800"} relative`}>
                    <div className="aspect-video bg-black relative overflow-hidden">
                      <video
                        src={c.stream_url || `${BACKEND_URL}/api/clips/${c.id}/stream`}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                        playsInline
                      />
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-[11px] text-stone-300 truncate font-display uppercase">{c.sub_label || c.rider_horse_name || "Clip"}</div>
                      <div className="text-[10px] font-mono-tc text-stone-500">{fmtDuration(c.duration || 0)}</div>
                    </div>
                    <button
                      disabled={used || isAdding}
                      onClick={() => addFromClip(c.id, c.sub_label || "")}
                      className={`w-full py-1.5 text-[10px] uppercase tracking-widest border-t ${used ? "border-emerald-700/40 bg-emerald-900/20 text-emerald-300" : "border-stone-800 hover:bg-orange-700/20 hover:text-orange-400 text-stone-300"} transition-colors disabled:opacity-50`}
                      data-testid={`train-add-clip-${c.id}`}
                    >
                      {used ? <><Check size={10} className="inline mr-1" /> Added</> : isAdding ? "Adding…" : <><Plus size={10} className="inline mr-1" /> Add as Reference</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 pb-4 flex gap-2 border-t border-stone-800 pt-3">
          <div className="flex-1 text-[10px] uppercase tracking-widest text-stone-500">
            {frames.length === 0 && "Save at least 2 — ideally 3–5 from different angles"}
            {frames.length === 1 && "Add 2 more for best accuracy"}
            {frames.length === 2 && "Add 1–3 more from different angles for best accuracy"}
            {frames.length >= 3 && frames.length < 8 && `✓ ${frames.length} references saved — the AI is ready to recognize this rider`}
            {frames.length >= 8 && "Library is well-stocked — adding more may not help much"}
          </div>
          <button onClick={onClose} className="btn-primary" data-testid="train-done">Done</button>
        </div>
      </div>
    </div>
  );
}

function EditRiderProfileModal({ name, profile, onClose, onSaved }) {
  const navigate = useNavigate();
  const [newName, setNewName] = useState(name);
  const [bio, setBio] = useState(profile.bio || "");
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.post(`/rider/${encodeURIComponent(name)}/avatar-upload-url`, null, {
        params: { content_type: file.type || "image/jpeg" },
      });
      const { upload_url, public_url } = res.data;
      const put = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type || "image/jpeg" }, body: file });
      if (!put.ok) throw new Error("S3 upload failed");
      setAvatarUrl(public_url);
      toast.success("Avatar uploaded");
    } catch (err) {
      toast.error("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const trimmedNewName = (newName || "").trim();
    if (!trimmedNewName) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      // 1) Save avatar/bio on the current name first
      await api.put(`/rider/${encodeURIComponent(name)}/profile`, { avatar_url: avatarUrl, bio });

      // 2) If the name changed, rename everywhere then navigate to the new URL
      if (trimmedNewName !== name) {
        const r = await api.post(`/rider/${encodeURIComponent(name)}/rename`, { new_name: trimmedNewName });
        toast.success(
          `Renamed · updated ${r.data.clips_updated} clip(s) and ${r.data.cuts_updated} cut(s)`,
          { duration: 4000 }
        );
        navigate(`/rider/${encodeURIComponent(r.data.new_name)}`, { replace: true });
        return;
      }
      toast.success("Profile saved");
      onSaved();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-profile-modal">
      <div className="bg-stone-950 border border-stone-700 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div className="font-display uppercase text-lg">Edit Profile</div>
          <button onClick={onClose} className="btn-secondary" data-testid="profile-modal-close"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="label-tiny mb-1">Rider / Horse Name</div>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Sarah Smith / Streakin Lover"
              className="field-input"
              data-testid="profile-name-input"
            />
            {newName.trim() !== name && newName.trim() && (
              <div className="text-[10px] text-amber-400 mt-1">
                ⚠ Renaming will update every clip, cut and tracking job tagged with "{name}".
              </div>
            )}
          </div>
          <div>
            <div className="label-tiny mb-2">Avatar</div>
            <div className="flex items-center gap-4">
              {avatarUrl ? (
                <img src={resolveAvatar(avatarUrl)} alt="" className="w-20 h-20 object-cover border-2 border-orange-600" />
              ) : (
                <div className="w-20 h-20 bg-stone-900 border-2 border-stone-800 flex items-center justify-center text-stone-600">
                  <Camera size={24} />
                </div>
              )}
              <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                <Camera size={12} />
                {uploading ? "Uploading…" : avatarUrl ? "Change" : "Upload Photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} disabled={uploading} data-testid="avatar-file-input" />
              </label>
              {avatarUrl && (
                <button onClick={() => setAvatarUrl("")} className="text-stone-500 hover:text-rose-400 text-xs" data-testid="avatar-remove-btn">Remove</button>
              )}
            </div>
            <div className="text-[10px] text-stone-500 mt-1">JPG/PNG, square works best.</div>
          </div>
          <div>
            <div className="label-tiny mb-1">Bio (optional)</div>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className="field-input" placeholder="Pro barrel racer · Alberta, Canada · NFR qualifier 2025" data-testid="profile-bio-input" />
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="profile-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="profile-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, accent = "stone" }) {
  const colorMap = {
    stone: "text-stone-100",
    rose: "text-rose-400",
    emerald: "text-emerald-400",
    orange: "text-orange-500",
  };
  return (
    <div className="border border-stone-800 px-4 py-3" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="label-tiny flex items-center gap-1.5">{icon} {label}</div>
      <div className={`font-display text-3xl font-bold mt-1 ${colorMap[accent]}`}>{value || 0}</div>
    </div>
  );
}

function PerformanceTrend({ trend }) {
  // Pick the metric the user wants to see plotted. Single-event riders just
  // get a stat summary since there's nothing to "trend" against yet.
  const [metric, setMetric] = useState("clean_pct");
  const single = trend.length < 2;

  if (single) {
    const p = trend[0];
    return (
      <div className="border border-stone-800 mb-8" data-testid="performance-trend">
        <div className="px-4 py-3 border-b border-stone-800 flex items-center gap-2 label-tiny">
          <TrendingUp size={12} className="text-orange-500" /> Performance Trend
        </div>
        <div className="p-5 text-center">
          <p className="text-stone-400 text-sm mb-3">
            Only one event recorded — keep clipping new events to unlock the trend line.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
            <MiniMetric label="Avg run" value={`${p.avg_duration.toFixed(2)}s`} />
            <MiniMetric label="Clean rate" value={`${p.clean_pct.toFixed(0)}%`} accent="emerald" />
            <MiniMetric label="Thumbs up" value={`${p.thumbs_up_pct.toFixed(0)}%`} accent="orange" />
            <MiniMetric label="Engagement" value={p.engagement} />
          </div>
        </div>
      </div>
    );
  }

  const cfg = {
    clean_pct:     { label: "Clean run %",        unit: "%",   color: "#10b981" },
    avg_duration:  { label: "Avg run duration",   unit: "s",   color: "#f97316", invert: true },
    thumbs_up_pct: { label: "Thumbs-up rate",     unit: "%",   color: "#fb923c" },
    engagement:    { label: "Total engagement",   unit: "",    color: "#a78bfa" },
  };
  const active = cfg[metric];
  const vals = trend.map((p) => p[metric] ?? 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = Math.max(1, max - min);
  const W = 720;
  const H = 200;
  const PAD_L = 36;
  const PAD_R = 16;
  const PAD_T = 12;
  const PAD_B = 36;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const stepX = trend.length > 1 ? innerW / (trend.length - 1) : 0;

  const points = trend.map((p, i) => {
    const v = p[metric] ?? 0;
    const ratio = (v - min) / span;
    const x = PAD_L + i * stepX;
    const y = PAD_T + (1 - ratio) * innerH;
    return { x, y, p, v };
  });

  const pathD = points.map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(" ");
  const areaD = `${pathD} L${points[points.length - 1].x.toFixed(1)},${PAD_T + innerH} L${points[0].x.toFixed(1)},${PAD_T + innerH} Z`;

  // Direction arrow vs first point
  const direction = points.length >= 2 ? (points[points.length - 1].v - points[0].v) : 0;
  const directionLabel = active.invert
    ? (direction < 0 ? "↓ Faster" : direction > 0 ? "↑ Slower" : "—")
    : (direction > 0 ? "↑ Trending up" : direction < 0 ? "↓ Trending down" : "—");
  const directionColor = active.invert
    ? (direction < 0 ? "text-emerald-400" : direction > 0 ? "text-rose-400" : "text-stone-500")
    : (direction > 0 ? "text-emerald-400" : direction < 0 ? "text-rose-400" : "text-stone-500");

  return (
    <div className="border border-stone-800 mb-8" data-testid="performance-trend">
      <div className="px-4 py-3 border-b border-stone-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 label-tiny">
          <TrendingUp size={12} className="text-orange-500" /> Performance Trend
          <span className="text-stone-600">·</span>
          <span className="text-stone-400">{trend.length} events</span>
          <span className={`ml-2 font-mono-tc text-xs ${directionColor}`}>{directionLabel}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {Object.entries(cfg).map(([k, c]) => (
            <button
              key={k}
              onClick={() => setMetric(k)}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${
                metric === k
                  ? "border-orange-700 text-orange-300 bg-orange-900/20"
                  : "border-stone-800 text-stone-500 hover:text-stone-200 hover:border-stone-600"
              }`}
              data-testid={`trend-metric-${k}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[600px]" preserveAspectRatio="xMidYMid meet">
          {/* Y-axis ticks */}
          {[0, 0.5, 1].map((r) => {
            const y = PAD_T + (1 - r) * innerH;
            const val = min + r * span;
            return (
              <g key={r}>
                <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} stroke="#292524" strokeDasharray="2 4" />
                <text x={PAD_L - 6} y={y + 4} fontSize="10" textAnchor="end" fill="#78716c" fontFamily="ui-monospace, monospace">
                  {val.toFixed(metric === "engagement" ? 0 : 1)}{active.unit}
                </text>
              </g>
            );
          })}

          {/* Area fill under the curve */}
          <path d={areaD} fill={active.color} opacity="0.12" />
          {/* Line */}
          <path d={pathD} fill="none" stroke={active.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

          {/* Points + labels */}
          {points.map((pt, i) => (
            <g key={i}>
              <circle cx={pt.x} cy={pt.y} r="4" fill="#0c0a09" stroke={active.color} strokeWidth="2" />
              <title>{`${pt.p.event_name}\n${pt.p.event_date || "(no date)"}\n${active.label}: ${pt.v}${active.unit}\nClips: ${pt.p.clip_count}`}</title>
              {/* X-axis label */}
              <text
                x={pt.x}
                y={H - 16}
                fontSize="10"
                textAnchor="middle"
                fill="#a8a29e"
                fontFamily="ui-monospace, monospace"
              >
                {pt.p.event_date || `E${i + 1}`}
              </text>
              <text
                x={pt.x}
                y={H - 4}
                fontSize="9"
                textAnchor="middle"
                fill="#57534e"
                fontFamily="ui-monospace, monospace"
              >
                {pt.p.clip_count} clip{pt.p.clip_count !== 1 ? "s" : ""}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {/* Per-event compact table */}
      <div className="border-t border-stone-800 px-4 py-3 overflow-x-auto">
        <table className="w-full text-xs font-mono-tc">
          <thead>
            <tr className="text-stone-500 border-b border-stone-800">
              <th className="text-left py-1.5 pr-3">Event</th>
              <th className="text-right py-1.5 px-3">Clips</th>
              <th className="text-right py-1.5 px-3">Avg run</th>
              <th className="text-right py-1.5 px-3">Clean</th>
              <th className="text-right py-1.5 px-3">👍</th>
              <th className="text-right py-1.5 pl-3">Engagement</th>
            </tr>
          </thead>
          <tbody>
            {trend.map((p, i) => (
              <tr key={i} className="border-b border-stone-900/60 last:border-0 hover:bg-stone-900/40">
                <td className="py-1.5 pr-3 truncate text-stone-300 max-w-xs">{p.event_name}{p.event_date && <span className="text-stone-600"> · {p.event_date}</span>}</td>
                <td className="py-1.5 px-3 text-right text-stone-400">{p.clip_count}</td>
                <td className="py-1.5 px-3 text-right text-stone-400">{p.avg_duration.toFixed(2)}s</td>
                <td className="py-1.5 px-3 text-right text-emerald-400">{p.clean_pct.toFixed(0)}%</td>
                <td className="py-1.5 px-3 text-right text-orange-400">{p.thumbs_up}{p.thumbs_down ? <span className="text-stone-500"> / {p.thumbs_down}</span> : ""}</td>
                <td className="py-1.5 pl-3 text-right text-purple-400">{p.engagement}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniMetric({ label, value, accent = "stone" }) {
  const c = accent === "emerald" ? "text-emerald-400"
          : accent === "orange"  ? "text-orange-400"
          : "text-stone-200";
  return (
    <div className="border border-stone-800 px-3 py-2">
      <div className={`font-display text-2xl font-bold ${c}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">{label}</div>
    </div>
  );
}


function LazyVideoPreview({ src }) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  useEffect(() => {
    if (shouldLoad || !ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
          break;
        }
      }
    }, { rootMargin: "300px" });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [shouldLoad]);
  return (
    <div ref={ref} className="absolute inset-0">
      {shouldLoad ? (
        <video src={src} className="w-full h-full object-contain" preload="metadata" muted playsInline />
      ) : (
        <div className="w-full h-full bg-stone-900 flex items-center justify-center">
          <Play size={36} className="text-stone-700" fill="currentColor" />
        </div>
      )}
    </div>
  );
}

function RiderClipCard({ clip, onPreview }) {
  const reacts = Object.entries(clip.reactions || {}).filter(([, n]) => n > 0);
  return (
    <div className="border border-stone-800 hover:border-orange-700 transition-colors group" data-testid={`rider-clip-${clip.id}`}>
      <button
        onClick={onPreview}
        className="block w-full aspect-video bg-black relative overflow-hidden"
      >
        <LazyVideoPreview src={clip.stream_url || `${BACKEND_URL}/api/clips/${clip.id}/stream`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Play size={32} className="text-white" fill="white" />
        </div>
        <div className="absolute top-2 left-2">
          <span className="tag-cat backdrop-blur-sm bg-stone-900/80">{CATEGORY_NAME[clip.category]}</span>
        </div>
        <div className="absolute bottom-2 right-2 font-mono-tc text-xs text-orange-400 bg-black/70 px-2 py-0.5">
          {fmtDuration(clip.duration)}
        </div>
      </button>
      <div className="p-3">
        <div className="font-display uppercase font-semibold text-base leading-tight truncate">
          {clip.sub_label || "Run"}
        </div>
        <div className="flex items-center gap-3 mt-2 font-mono-tc text-xs text-stone-500">
          <span className="inline-flex items-center gap-1"><Eye size={11} /> {clip.views || 0}</span>
          <span className="inline-flex items-center gap-1"><MessageSquare size={11} /> {clip.comments_count || 0}</span>
          {reacts.slice(0, 4).map(([e, n]) => (
            <span key={e} className="inline-flex items-center gap-1"><span>{REACTION_EMOJI[e]}</span> {n}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ClipFeedbackModal({ clip, onClose, onChanged }) {
  const [counts, setCounts] = useState({});
  const [comments, setComments] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("rcs_visitor_name") || "");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [viewed, setViewed] = useState(false);

  useEffect(() => {
    api.get(`/clips/${clip.id}/reactions`).then((r) => setCounts(r.data.counts || {}));
    api.get(`/clips/${clip.id}/comments`).then((r) => setComments(r.data.comments || []));
  }, [clip.id]);

  const handlePlay = () => {
    if (viewed) return;
    setViewed(true);
    api.post(`/clips/${clip.id}/view`).catch(() => {});
  };

  const react = async (emoji) => {
    try {
      const r = await api.post(`/clips/${clip.id}/reactions`, { emoji, by_name: name });
      setCounts(r.data.counts);
      onChanged && onChanged();
    } catch {
      toast.error("Failed to react");
    }
  };

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/clips/${clip.id}/comments`, { text, by_name: name || "Anonymous" });
      setComments((prev) => [r.data.comment, ...prev]);
      if (name) localStorage.setItem("rcs_visitor_name", name);
      setText("");
      onChanged && onChanged();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="clip-feedback-modal">
      <div className="bg-stone-950 border border-stone-700 max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800 sticky top-0 bg-stone-950 z-10">
          <div className="min-w-0">
            <div className="label-tiny">{CATEGORY_NAME[clip.category]}</div>
            <div className="font-display uppercase text-xl truncate">
              {clip.sub_label || "Run"} {clip.rider_horse_name && <span className="text-orange-400">· {clip.rider_horse_name}</span>}
            </div>
          </div>
          <button onClick={onClose} className="btn-secondary" data-testid="modal-close"><X size={14} /></button>
        </div>

        <video
          src={clip.stream_url || `${BACKEND_URL}/api/clips/${clip.id}/stream`}
          controls autoPlay
          onPlay={handlePlay}
          className="w-full bg-black"
        />

        {clip.notes && (
          <div className="px-4 py-3 text-sm text-stone-300 border-b border-stone-800 italic">{clip.notes}</div>
        )}

        <div className="px-4 py-4 border-b border-stone-800" data-testid="reactions-row">
          <div className="label-tiny mb-2">React</div>
          <div className="flex flex-wrap gap-2">
            {REACTIONS.map((r) => (
              <button
                key={r.id}
                onClick={() => react(r.id)}
                className="btn-secondary !py-2 !px-3 inline-flex items-center gap-2 hover:!border-orange-700"
                data-testid={`react-${r.id}`}
              >
                <span className="text-lg">{r.emoji}</span>
                <span className="font-mono-tc text-stone-300">{counts[r.id] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 py-4">
          <div className="label-tiny mb-3 flex items-center gap-2">
            <MessageSquare size={12} /> Comments ({comments.length})
          </div>

          <form onSubmit={post} className="space-y-2 mb-4" data-testid="comment-form">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="field-input"
              data-testid="comment-name"
            />
            <div className="flex gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Leave a coaching note, encouragement, or question…"
                className="field-input flex-1"
                data-testid="comment-text"
              />
              <button type="submit" disabled={posting || !text.trim()} className="btn-primary inline-flex items-center gap-1.5">
                <Send size={12} /> Post
              </button>
            </div>
          </form>

          <div className="space-y-3">
            {comments.length === 0 && (
              <div className="text-stone-500 text-sm italic">No comments yet — be first.</div>
            )}
            {comments.map((c) => (
              <div key={c.id} className="border-l-2 border-stone-800 pl-3" data-testid={`comment-${c.id}`}>
                <div className="text-xs text-stone-500 font-mono-tc mb-0.5">
                  <span className="text-orange-400">{c.by_name}</span> · {(c.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
                <div className="text-sm text-stone-200 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
